import { motion } from "framer-motion";

/**
 * Bloom Plant — grows with each drop of water.
 * `growth` 0–3 scales the stem/leaves; a flower blooms at 3.
 * `mood`: idle | happy | confused | celebrating drives the pot's little face.
 */
export function Plant({ growth = 0, mood = "idle", className }) {
  const g = Math.min(Math.max(growth, 0), 3);
  const scale = 0.42 + g * 0.193; // 0.42 (sprout) → 1.0 (full)
  const bright = g >= 3;
  const happy = mood === "happy" || mood === "celebrating";
  const confused = mood === "confused";
  const leaf = bright ? "#2FBE74" : "#69BE92";
  const leafD = bright ? "#1EA362" : "#509E78";

  return (
    <svg viewBox="0 0 200 240" className={className} aria-hidden focusable="false">
      {/* plant top — scales from the pot rim as it grows */}
      <motion.g
        style={{ transformBox: "view-box", transformOrigin: "100px 150px" }}
        animate={{ scale }}
        transition={{ type: "spring", stiffness: 160, damping: 14 }}
      >
        <path d="M100 152 C 96 110, 100 80, 100 40" stroke={leafD} strokeWidth="8" strokeLinecap="round" fill="none" style={{ transition: "stroke .4s" }} />
        <path d="M100 112 C 70 102, 54 78, 58 60 C 80 62, 99 86, 100 112 Z" fill={leaf} style={{ transition: "fill .4s" }} />
        <path d="M100 94 C 130 86, 146 62, 142 46 C 120 50, 101 70, 100 94 Z" fill={leaf} style={{ transition: "fill .4s" }} />
        {/* flower (blooms at full growth) */}
        <motion.g
          style={{ transformBox: "view-box", transformOrigin: "100px 36px" }}
          animate={{ opacity: bright ? 1 : 0, scale: bright ? 1 : 0.2 }}
          transition={{ type: "spring", stiffness: 300, damping: 12 }}
        >
          {[0, 72, 144, 216, 288].map((a) => (
            <ellipse key={a} cx="100" cy="22" rx="9" ry="14" fill="#FF9EC4" transform={`rotate(${a} 100 36)`} />
          ))}
          <circle cx="100" cy="36" r="9" fill="#FFC94D" />
        </motion.g>
      </motion.g>

      {/* pot */}
      <path d="M62 150 L138 150 L128 224 C128 230 122 232 100 232 C78 232 72 230 72 224 Z" fill="#E08A5B" />
      <path d="M58 145 L142 145 L138 161 L62 161 Z" fill="#EE9E6E" />
      <ellipse cx="100" cy="150" rx="38" ry="6" fill="#7A4A2B" />

      {/* cheeks */}
      <ellipse cx="80" cy="196" rx="8" ry="5" fill="#F2A98A" opacity="0.8" />
      <ellipse cx="120" cy="196" rx="8" ry="5" fill="#F2A98A" opacity="0.8" />

      {/* eyes */}
      {happy ? (
        <>
          <path d="M82 186 Q88 179 94 186" stroke="#5A3A22" strokeWidth="4" strokeLinecap="round" fill="none" />
          <path d="M106 186 Q112 179 118 186" stroke="#5A3A22" strokeWidth="4" strokeLinecap="round" fill="none" />
        </>
      ) : confused ? (
        <>
          <path d="M82 184 Q88 180 94 185" stroke="#5A3A22" strokeWidth="3.5" strokeLinecap="round" fill="none" />
          <circle cx="113" cy="186" r="4" fill="#5A3A22" />
        </>
      ) : (
        <>
          <circle cx="88" cy="186" r="4" fill="#5A3A22" />
          <circle cx="113" cy="186" r="4" fill="#5A3A22" />
        </>
      )}

      {/* mouth */}
      {mood === "celebrating" ? (
        <path d="M88 200 Q100 214 112 200 Q100 208 88 200 Z" fill="#5A3A22" />
      ) : happy ? (
        <path d="M89 201 Q100 211 111 201" stroke="#5A3A22" strokeWidth="4" strokeLinecap="round" fill="none" />
      ) : confused ? (
        <path d="M88 203 Q94 197 100 203 Q106 209 112 203" stroke="#5A3A22" strokeWidth="3.5" strokeLinecap="round" fill="none" />
      ) : (
        <path d="M91 201 Q100 207 109 201" stroke="#5A3A22" strokeWidth="3.5" strokeLinecap="round" fill="none" />
      )}
    </svg>
  );
}

export default Plant;
